import { useState, useEffect, useMemo } from 'react';
import {
  getSubscriptions,
  enrollPlayers,
  cancelSubscription,
  sendPaymentLink,
  getCollectionsStatus,
  getMemberships,
  getPlayers,
  getMembers,
  getGuardianships,
  getTeams,
  createMember,
  linkGuardian,
  Subscription,
  CollectionStatus,
  Membership,
  Player,
  Member,
  Guardianship,
  Team,
  EnrollmentInput,
  updateMember,
} from '../api/client';
import CollectionsNav from '../components/CollectionsNav';
import { describeSubscriptionCharge, describePlan } from '../utils/charge';

/**
 * Collections → Enrolments. The per-player subscriptions: who is enrolled in
 * which plan, billed to which payer, and where each sits in its lifecycle.
 * The "Enrol players" wizard creates them (single or bulk), defaulting the
 * payer to each player's primary guardian. Gated on features.collections.
 *
 * Enrolments land in 'pending', the payer then saves a card
 * (next chunk) before charges begin.
 */

const STATUS_LABEL: Record<CollectionStatus, string> = {
  // 'active', 'past_due' and 'paused' only appear on rows written before
  // 2026-08-25, when a fee stopped being a subscription.
  pending: 'Not paid', completed: 'Paid ✓', cancelled: 'Cancelled',
  active: 'Active (legacy)', past_due: 'Past due (legacy)', paused: 'Paused (legacy)',
};


export default function Subscriptions() {
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [plans, setPlans] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState<CollectionStatus | ''>('');
  const [busyId, setBusyId] = useState('');
  const [showEnrol, setShowEnrol] = useState(false);
  const [emailFor, setEmailFor] = useState<Subscription | null>(null);
  const [bulkSel, setBulkSel] = useState<Record<string, boolean>>({});
  const [bulkSending, setBulkSending] = useState(false);
  const [sentPayers, setSentPayers] = useState<Record<string, boolean>>({}); // payerIds links were sent to this session
  const [gcReady, setGcReady] = useState(true); // payments connected, gates sending links

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([getSubscriptions(), getMemberships()]);
      setSubs(s);
      setPlans(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load enrolments');
    } finally {
      setLoading(false);
    }
    // Non-blocking: can the club actually collect yet?
    getCollectionsStatus().then(g => setGcReady(!!(g.connected && g.verified))).catch(() => {});
  }

  const planName = (id: string) => plans.find(p => p.membershipId === id)?.name || '';
  const visible = statusFilter ? subs.filter(s => s.status === statusFilter) : subs;

  // Bulk send: pending enrolments whose payer has an email. We dedupe by payer
  // (one saved card per payer covers all their enrolments).
  const linkable = visible.filter(s => s.status === 'pending' && !!s.payerEmail);
  const selectedSubs = visible.filter(s => bulkSel[s.subscriptionId]);
  const uniquePayers = Array.from(new Set(selectedSubs.map(s => s.payerId).filter(Boolean)));
  const allLinkableSelected = linkable.length > 0 && linkable.every(s => bulkSel[s.subscriptionId]);

  function toggleAllLinkable() {
    if (allLinkableSelected) { setBulkSel({}); return; }
    const next: Record<string, boolean> = {};
    linkable.forEach(s => { next[s.subscriptionId] = true; });
    setBulkSel(next);
  }

  async function bulkSend() {
    if (!uniquePayers.length) return;
    setBulkSending(true);
    setError('');
    const results = await Promise.allSettled(uniquePayers.map(p => sendPaymentLink(p)));
    const ok = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.length - ok;
    const sentNow: Record<string, boolean> = {};
    results.forEach((r, i) => { const p = uniquePayers[i]; if (p && r.status === 'fulfilled') sentNow[p] = true; });
    setSentPayers(prev => ({ ...prev, ...sentNow }));
    setSuccess(`Sent ${ok} payment link${ok === 1 ? '' : 's'}${failed ? ` · ${failed} failed` : ''}`);
    setBulkSel({});
    setBulkSending(false);
    await load();
    setTimeout(() => setSuccess(''), 5000);
  }

  async function sendLink(sub: Subscription) {
    if (!sub.payerId) { setError('This enrolment has no payer to send a link to'); return; }
    setBusyId(sub.subscriptionId);
    setError('');
    try {
      const r = await sendPaymentLink(sub.payerId);
      setSentPayers(prev => ({ ...prev, [sub.payerId]: true }));
      setSuccess(`Payment link sent to ${r.sentTo}`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send payment link');
    } finally {
      setBusyId('');
    }
  }

  async function doAction(sub: Subscription, action: 'cancel') {
    if (action === 'cancel' && !confirm(`Cancel ${sub.playerName || 'this'} enrolment in "${sub.membershipName || planName(sub.membershipId)}"? Any live subscription is cancelled too.`)) return;
    setBusyId(sub.subscriptionId);
    setError('');
    try {
      const fn = cancelSubscription;
      await fn(sub.subscriptionId);
      setSuccess(`${sub.playerName || 'Enrolment'} cancelled`);
      await load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to ${action}`);
    } finally {
      setBusyId('');
    }
  }

  return (
    <div className="tm-page-shell">
      <CollectionsNav />
      <div className="page-head">
        <div>
          <h1>Enrolments</h1>
          <p className="subtitle">Players enrolled into a plan, each billed to a payer. Enrol players to start collecting.</p>
        </div>
        <div className="head-actions">
          <button className="ebtn ebtn-primary" onClick={() => setShowEnrol(true)} disabled={plans.length === 0}>
            + Enrol players
          </button>
        </div>
      </div>

      {plans.length === 0 && !loading && (
        <div className="ealert ealert-info">Create a membership plan first, then enrol players into it.</div>
      )}
      {!gcReady && (
        <div className="ealert ealert-info">
          Finish your payment setup, <strong>connect and verify</strong>, before sending payment links. You can enrol players now and send links once it's ready. <a className="tm-link" href="/collections">Open setup →</a>
        </div>
      )}
      {error && <div className="ealert ealert-error">{error}</div>}
      {success && <div className="ealert ealert-success">{success}</div>}

      <div className="tm-filters">
        <label className="tm-filter">
          Status:&nbsp;
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as CollectionStatus | '')}>
            <option value="">All</option>
            {(Object.keys(STATUS_LABEL) as CollectionStatus[]).map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </label>
        <span className="tm-muted" style={{ marginLeft: 'auto' }}>{visible.length} enrolment{visible.length === 1 ? '' : 's'}</span>
      </div>

      {selectedSubs.length > 0 && (
        <div className="col-bulkbar">
          <span className="col-bulkbar-count">{selectedSubs.length} selected</span>
          <span className="tm-muted">→ {uniquePayers.length} payment link{uniquePayers.length === 1 ? '' : 's'} (one per payer)</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="ebtn ebtn-secondary ebtn-sm" onClick={() => setBulkSel({})} disabled={bulkSending}>Clear</button>
            <button className="ebtn ebtn-primary ebtn-sm" onClick={bulkSend} disabled={bulkSending || !gcReady} title={!gcReady ? 'Finish payment setup first' : undefined}>
              {bulkSending ? 'Sending…' : `Send ${uniquePayers.length} link${uniquePayers.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="loading">Loading enrolments…</p>
      ) : visible.length === 0 ? (
        <div className="empty-state"><p>No enrolments yet.</p></div>
      ) : (
        <div className="card-surface">
          <table className="tm-table">
            <thead>
              <tr>
                <th style={{ width: 34 }}>
                  {linkable.length > 0 && (
                    <input type="checkbox" checked={allLinkableSelected} onChange={toggleAllLinkable}
                      title="Select all enrolments awaiting a card, with an email" />
                  )}
                </th>
                <th>Player</th>
                <th>Plan</th>
                <th>Payer</th>
                <th>Charge</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map(s => (
                <tr key={s.subscriptionId}>
                  <td>
                    {s.status === 'pending' && s.payerEmail && (
                      <input type="checkbox" checked={!!bulkSel[s.subscriptionId]}
                        onChange={e => setBulkSel({ ...bulkSel, [s.subscriptionId]: e.target.checked })} />
                    )}
                  </td>
                  <td><span className="tm-person-name">{s.playerName || s.playerId}</span></td>
                  <td>{s.membershipName || planName(s.membershipId)}</td>
                  <td>
                    {s.payerName || <span className="tm-muted">-</span>}
                    {s.payerEmail && <div className="tm-muted" style={{ fontSize: '0.8em' }}>{s.payerEmail}</div>}
                  </td>
                  <td>
                    {describeSubscriptionCharge(s).summary}
                    {s.priceOverride != null && <span className="tm-chip" style={{ marginLeft: 6 }}>override</span>}
                  </td>
                  <td><span className={`col-pill is-${s.status}`}>{STATUS_LABEL[s.status]}</span></td>
                  <td>
                    <div className="tm-actions">
                      {s.status === 'pending' && (s.payerEmail
                        ? (sentPayers[s.payerId]
                            ? <><span className="col-verified">✓ Link sent</span><button className="ebtn ebtn-secondary ebtn-sm" disabled={busyId === s.subscriptionId || !gcReady} title={!gcReady ? 'Finish payment setup first' : undefined} onClick={() => sendLink(s)}>Resend</button></>
                            : <button className="ebtn ebtn-primary ebtn-sm" disabled={busyId === s.subscriptionId || !gcReady} title={!gcReady ? 'Finish payment setup first' : undefined} onClick={() => sendLink(s)}>Send link</button>)
                        : <button className="ebtn ebtn-secondary ebtn-sm" onClick={() => setEmailFor(s)}>Add email</button>
                      )}
                      {s.status !== 'cancelled' && s.status !== 'completed' && (
                        <button className="ebtn ebtn-danger ebtn-sm" disabled={busyId === s.subscriptionId} onClick={() => doAction(s, 'cancel')}>Cancel</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showEnrol && (
        <EnrolModal
          plans={plans.filter(p => p.active)}
          onClose={() => setShowEnrol(false)}
          onDone={(msg) => { setShowEnrol(false); setSuccess(msg); void load(); setTimeout(() => setSuccess(''), 5000); }}
        />
      )}

      {emailFor && (
        <AddEmailModal
          payerId={emailFor.payerId}
          payerName={emailFor.payerName}
          onClose={() => setEmailFor(null)}
          onDone={(msg) => { setEmailFor(null); setSuccess(msg); void load(); setTimeout(() => setSuccess(''), 4000); }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────
// Add-email, set a payer's email inline so they can be sent a payment link.
// ────────────────────────────────────────

function AddEmailModal({ payerId, payerName, onClose, onDone }: {
  payerId: string;
  payerName: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function save() {
    const e = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { setErr('Enter a valid email address'); return; }
    setSaving(true);
    setErr('');
    try {
      await updateMember(payerId, { email: e });
      onDone(`Email added for ${payerName || 'payer'}, you can now send their payment link`);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Failed to save email');
      setSaving(false);
    }
  }

  return (
    <div className="tm-modal-overlay" onClick={onClose}>
      <div className="tm-modal" onClick={e => e.stopPropagation()}>
        <div className="tm-modal-header">
          <h2>Add email for {payerName || 'payer'}</h2>
          <button type="button" className="tm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="tm-modal-body">
          {err && <div className="ealert ealert-error">{err}</div>}
          <p className="tm-muted" style={{ marginTop: 0 }}>The payment link is emailed to the payer, so they need an address on file.</p>
          <label className="tm-field">
            <span>Email address</span>
            <input type="email" value={email} autoFocus onChange={e => setEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void save(); }} placeholder="parent@example.com" />
          </label>
        </div>
        <div className="tm-modal-actions">
          <button type="button" className="ebtn ebtn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="ebtn ebtn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save email'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────
// Enrol wizard, pick a plan, pick players, confirm payers, enrol.
// ────────────────────────────────────────

function EnrolModal({ plans, onClose, onDone }: {
  plans: Membership[];
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [planId, setPlanId] = useState(plans[0]?.membershipId || '');
  const [players, setPlayers] = useState<Player[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [guardianships, setGuardianships] = useState<Guardianship[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  // Inline "add payer" form state (keyed to the player we're adding one for).
  const [addPayerFor, setAddPayerFor] = useState<string | null>(null);
  const [npExisting, setNpExisting] = useState(''); // link an existing member, or…
  const [npName, setNpName] = useState('');         // …create a new one
  const [npEmail, setNpEmail] = useState('');
  const [npSaving, setNpSaving] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [payerOverride, setPayerOverride] = useState<Record<string, string>>({});
  const [priceOverridePounds, setPriceOverridePounds] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [rowErrors, setRowErrors] = useState<{ playerId?: string; error: string }[]>([]);

  const plan = plans.find(p => p.membershipId === planId);

  useEffect(() => {
    setLoading(true);
    // Degrade gracefully: players load on their own; members/guardianships are
    // gated on the Members module, if it's off they 404, so don't let that
    // blow up the whole wizard, just flag it.
    Promise.allSettled([getPlayers(), getMembers(), getGuardianships(), getTeams()])
      .then(([rp, rm, rg, rt]) => {
        if (rp.status === 'fulfilled') setPlayers(rp.value);
        if (rm.status === 'fulfilled') setMembers(rm.value);
        if (rg.status === 'fulfilled') setGuardianships(rg.value);
        if (rt.status === 'fulfilled') setTeams(rt.value);
        if (rp.status === 'rejected') setErr('Could not load players for this club.');
        else if (rm.status === 'rejected' || rg.status === 'rejected') {
          setErr('The Members module isn’t enabled for this club, so payers can’t be set. Enable Members to enrol.');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Primary-guardian-by-player (fall back to any guardian) → the default payer.
  const primaryByPlayer = useMemo(() => {
    const out: Record<string, string> = {};
    for (const g of guardianships) if (g.role === 'primary' && !out[g.playerId]) out[g.playerId] = g.memberId;
    for (const g of guardianships) if (!out[g.playerId]) out[g.playerId] = g.memberId;
    return out;
  }, [guardianships]);

  const memberName = (id: string) => members.find(m => m.memberId === id)?.name || '';

  // Team-scoped plans lock the list to that team; otherwise the admin can filter.
  const lockedTeam = plan?.teamId || '';
  const eligible = useMemo(() => {
    let list = players.filter(p => p.status !== 'archived');
    const teamId = lockedTeam || teamFilter;
    if (teamId) list = list.filter(p => p.teamId === teamId);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => `${p.firstName} ${p.lastName}`.toLowerCase().includes(q));
    }
    return list.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
  }, [players, lockedTeam, teamFilter, search]);

  // Teams that actually have players, for the filter dropdown.
  const teamOptions = useMemo(() => {
    const ids = new Set(players.filter(p => p.status !== 'archived').map(p => p.teamId).filter(Boolean));
    return teams.filter(t => ids.has(t.id));
  }, [players, teams]);

  const selectedIds = Object.keys(selected).filter(id => selected[id]);

  function payerFor(playerId: string): string {
    return payerOverride[playerId] || primaryByPlayer[playerId] || '';
  }

  // Member ids linked as a guardian of this player (to label dropdown options).
  function guardianIdsFor(playerId: string): Set<string> {
    return new Set(guardianships.filter(g => g.playerId === playerId).map(g => g.memberId));
  }

  const allEligibleSelected = eligible.length > 0 && eligible.every(p => selected[p.playerId]);
  function toggleSelectAll() {
    const next = { ...selected };
    eligible.forEach(p => { next[p.playerId] = !allEligibleSelected; });
    setSelected(next);
  }

  // Block enrolment while any selected player has no payer.
  const selectedMissingPayer = selectedIds.filter(id => !payerFor(id));

  // "+ Add payer": make a real connection between a payer and THIS player,
  // either link an existing club member, or create a new one and link them.
  // The payer is therefore always someone tied to the child (no rando-pays-for-
  // rando-child, no fat-finger wrong payer). Links as primary if the player has
  // none yet, else secondary.
  async function saveNewPayer(playerId: string) {
    setErr('');
    const name = npName.trim();
    const email = npEmail.trim();
    if (!npExisting && !name) { setErr('Pick an existing member, or enter a new payer’s name'); return; }
    if (!npExisting && email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setErr('Enter a valid email (or leave it blank)'); return; }
    setNpSaving(true);
    try {
      const hasPrimary = guardianships.some(g => g.playerId === playerId && g.role === 'primary');
      const role: 'primary' | 'secondary' = hasPrimary ? 'secondary' : 'primary';
      let memberId = npExisting;
      if (!memberId) {
        const m = await createMember({ name, email: email || undefined, ageType: 'adult', roles: ['guardian', 'payer'] });
        setMembers(prev => [...prev, m]);
        memberId = m.memberId;
      }
      try { await linkGuardian(playerId, memberId, role); } catch { /* link is best-effort */ }
      setGuardianships(prev => [...prev, { playerId, memberId, role, consentMethod: '' }]);
      setPayerOverride(prev => ({ ...prev, [playerId]: memberId }));
      setSelected(prev => ({ ...prev, [playerId]: true }));
      setAddPayerFor(null); setNpName(''); setNpEmail(''); setNpExisting('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not add payer');
    } finally {
      setNpSaving(false);
    }
  }

  // What each selected player will be charged, honouring a price override.
  const overridePence = priceOverridePounds.trim() ? Math.round(parseFloat(priceOverridePounds) * 100) : null;
  const effTotal = overridePence && overridePence > 0 ? overridePence : (plan?.totalPrice ?? 0);
  const wizardCharge = plan ? { summary: `£${(effTotal / 100).toFixed(2)}` } : null;

  async function submit() {
    setErr('');
    setRowErrors([]);
    if (!planId) { setErr('Pick a plan'); return; }
    if (selectedIds.length === 0) { setErr('Select at least one player'); return; }
    const override = priceOverridePounds.trim() ? Math.round(parseFloat(priceOverridePounds) * 100) : undefined;
    if (override !== undefined && (!Number.isFinite(override) || override <= 0)) { setErr('Price override must be a valid amount'); return; }

    // Every selected player needs a payer, resolved from their guardian or
    // picked here. Catch it client-side so we don't fire a doomed request.
    const missing = selectedIds.filter(id => !payerFor(id));
    if (missing.length) {
      const names = missing.map(id => {
        const p = players.find(pl => pl.playerId === id);
        return p ? `${p.firstName} ${p.lastName}`.trim() : id;
      });
      setErr(`Pick a payer for: ${names.join(', ')}, no guardian on file. Use the payer dropdown beside each, or link a guardian first in Team Management.`);
      return;
    }

    const enrollments: EnrollmentInput[] = selectedIds.map(playerId => {
      const payerId = payerFor(playerId);
      return {
        playerId,
        membershipId: planId,
        ...(payerId ? { payerId } : {}),
        ...(override !== undefined ? { priceOverride: override } : {}),
      };
    });

    setSubmitting(true);
    try {
      const res = await enrollPlayers(enrollments);
      if (res.errorCount > 0) {
        setRowErrors(res.errors);
        if (res.createdCount === 0) { setErr('No enrolments created, see issues below'); setSubmitting(false); return; }
      }
      onDone(`Enrolled ${res.createdCount} player${res.createdCount === 1 ? '' : 's'}${res.errorCount ? ` (${res.errorCount} skipped)` : ''}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Enrolment failed');
      setSubmitting(false);
    }
  }

  return (
    <div className="tm-modal-overlay" onClick={onClose}>
      <div className="tm-modal tm-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="tm-modal-header">
          <h2>Enrol players</h2>
          <button type="button" className="tm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="tm-modal-body">
          {err && <div className="ealert ealert-error">{err}</div>}

          <label className="tm-field">
            <span>Plan</span>
            <select value={planId} onChange={e => { setPlanId(e.target.value); setSelected({}); setPriceOverridePounds(''); }}>
              {plans.map(p => (
                <option key={p.membershipId} value={p.membershipId}>
                  {p.name}{p.teamId ? ' (team)' : ''}, {describePlan(p).summary}
                </option>
              ))}
            </select>
          </label>

          {wizardCharge && (
            <div className="ealert ealert-info" style={{ margin: '4px 0 0' }}>
              Each selected player will be charged <strong>{wizardCharge.summary}</strong>, once.
            </div>
          )}

          {loading ? (
            <p className="loading">Loading players…</p>
          ) : (
            <>
              <div className="tm-form-row">
                <label className="tm-field">
                  <span>Find players</span>
                  <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name…" />
                </label>
                {!lockedTeam && teamOptions.length > 0 && (
                  <label className="tm-field">
                    <span>Filter by team</span>
                    <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)}>
                      <option value="">All teams</option>
                      {teamOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </label>
                )}
              </div>

              {players.length === 0 ? (
                <p className="tm-muted">No players in this club yet, add players in Team Management first, then come back to enrol them.</p>
              ) : eligible.length === 0 ? (
                <p className="tm-muted">No players{plan?.teamId ? ' in this plan’s team' : ''} match{search ? ' your search' : ''}.</p>
              ) : (
                <div className="card-surface" style={{ maxHeight: 320, overflowY: 'auto' }}>
                  <table className="tm-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>
                          <input type="checkbox" checked={allEligibleSelected} onChange={toggleSelectAll}
                            title={allEligibleSelected ? 'Clear all' : 'Select all shown'} />
                        </th>
                        <th>Player</th>
                        <th>Payer (default = primary guardian)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligible.map(p => {
                        const checked = !!selected[p.playerId];
                        const resolvedPayer = payerFor(p.playerId);
                        const gIds = guardianIdsFor(p.playerId);
                        const adding = addPayerFor === p.playerId;
                        // Payers offered = ONLY people connected to this player.
                        const linkedPayers = members.filter(m => m.status === 'active' && gIds.has(m.memberId));
                        const otherMembers = members.filter(m => m.status === 'active' && !gIds.has(m.memberId));
                        const openAdd = () => { setAddPayerFor(p.playerId); setNpExisting(''); setNpName(''); setNpEmail(''); setErr(''); };
                        return (
                          <tr key={p.playerId}>
                            <td>
                              <input type="checkbox" checked={checked} onChange={e => setSelected({ ...selected, [p.playerId]: e.target.checked })} />
                            </td>
                            <td><span className="tm-person-name">{p.firstName} {p.lastName}</span></td>
                            <td>
                              {checked ? (
                                <>
                                  {linkedPayers.length > 0 ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                      <select value={resolvedPayer} onChange={e => setPayerOverride({ ...payerOverride, [p.playerId]: e.target.value })}>
                                        <option value="">, select payer, </option>
                                        {linkedPayers.map(m => (
                                          <option key={m.memberId} value={m.memberId}>{m.name}{m.email ? `, ${m.email}` : ''}</option>
                                        ))}
                                      </select>
                                      {!adding && <button type="button" className="tm-link" style={{ fontSize: '0.8rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={openAdd}>+ Add payer</button>}
                                    </div>
                                  ) : (!adding && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                      <span style={{ color: '#b91c1c', fontSize: '0.8rem' }}>No payer linked to this player.</span>
                                      <button type="button" className="ebtn ebtn-primary ebtn-sm" onClick={openAdd}>+ Add payer</button>
                                    </div>
                                  ))}

                                  {adding && (
                                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                      {otherMembers.length > 0 && (
                                        <select value={npExisting} onChange={e => setNpExisting(e.target.value)}>
                                          <option value="">, link someone already at the club, </option>
                                          {otherMembers.map(m => <option key={m.memberId} value={m.memberId}>{m.name}{m.email ? `, ${m.email}` : ''}</option>)}
                                        </select>
                                      )}
                                      {!npExisting && (
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                          <input type="text" value={npName} onChange={e => setNpName(e.target.value)} placeholder="New payer name" style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem' }} />
                                          <input type="email" value={npEmail} onChange={e => setNpEmail(e.target.value)} placeholder="Email (for the link)" style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem' }} />
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', gap: 6 }}>
                                        <button type="button" className="ebtn ebtn-primary ebtn-sm" disabled={npSaving} onClick={() => saveNewPayer(p.playerId)}>{npSaving ? 'Adding…' : 'Add payer'}</button>
                                        <button type="button" className="ebtn ebtn-secondary ebtn-sm" disabled={npSaving} onClick={() => { setAddPayerFor(null); setNpExisting(''); }}>Cancel</button>
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span className="tm-muted">{resolvedPayer ? memberName(resolvedPayer) : 'no payer linked'}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="tm-form-row" style={{ marginTop: 12 }}>
                <label className="tm-field">
                  <span>Fee override (£, optional)</span>
                  <input type="number" min="0" step="0.01" value={priceOverridePounds} onChange={e => setPriceOverridePounds(e.target.value)}
                    placeholder={plan ? (plan.totalPrice / 100).toFixed(2) : ''} />
                </label>
              </div>

              {rowErrors.length > 0 && (
                <div className="ealert ealert-error">
                  <strong>Skipped:</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {rowErrors.map((e, i) => {
                      const p = players.find(pl => pl.playerId === e.playerId);
                      return <li key={i}>{p ? `${p.firstName} ${p.lastName}` : e.playerId || 'row'}: {e.error}</li>;
                    })}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
        <div className="tm-modal-actions">
          {selectedMissingPayer.length > 0 && (
            <span className="tm-muted" style={{ marginRight: 'auto', color: '#b91c1c' }}>
              {selectedMissingPayer.length} selected player{selectedMissingPayer.length === 1 ? '' : 's'} need a payer
            </span>
          )}
          <button type="button" className="ebtn ebtn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="ebtn ebtn-primary" onClick={submit}
            disabled={submitting || selectedIds.length === 0 || selectedMissingPayer.length > 0}>
            {submitting ? 'Enrolling…' : `Enrol ${selectedIds.length || ''} player${selectedIds.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
